import React, { useCallback, type PropsWithChildren } from 'react';

import { Box, Pressable } from '@atlaskit/primitives/compiled';

import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';

const featureItemStyles = cssMap({
	root: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
		paddingInline: token('space.200'),
		paddingBlock: token('space.100'),
		cursor: 'pointer',
		width: '100%',

		// @ts-expect-error - cssMap forces us to only use token('border.width')
		borderLeft: '2px solid transparent',
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
			borderLeftColor: token('color.border.selected'),
		},
	},
	box: {
		width: '100%',
	},
});

const featureItemPressableStyles = cssMap({
	root: {
		backgroundColor: 'transparent',
	},
});

const featureItemActiveStyles = cssMap({
	root: {
		color: token('color.text.selected'),
		backgroundColor: token('color.background.selected'),
		borderLeftColor: token('color.border.selected'),
	},
});
export interface FeatureItemProps extends PropsWithChildren {
	isActive?: boolean;
	onClick: (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
	index: number;
}
export const FeatureItem = ({
	children,
	isActive,
	onClick,
	index,
}: FeatureItemProps): React.JSX.Element => {
	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			onClick(e, analyticsEvent);
			fireUIAnalytics(analyticsEvent, 'featureItem', {
				index,
			});
		},
		[index, onClick],
	);

	return (
		<Box
			as="li"
			xcss={cx(featureItemStyles.box, isActive && featureItemActiveStyles.root)}
			data-active={isActive}
			aria-hidden="false"
			testId={`feature-item-${index}`}
		>
			<Pressable
				aria-pressed={isActive}
				onClick={handleClick}
				xcss={cx(featureItemStyles.root, featureItemPressableStyles.root)}
			>
				{children}
			</Pressable>
		</Box>
	);
};
